import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowUpRight, MapPin, BookOpen } from "lucide-react";
import api from "../lib/api";
import { Monogram } from "../components/Monogram";
import { StatusMark } from "../components/StatusMark";
import { formatDate, formatTime } from "../lib/format";

const HERO_IMG =
  "https://images.unsplash.com/photo-1583482183620-f692113aafc3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxlbXB0eSUyMGJsYWNrJTIwYm94JTIwdGhlYXRlciUyMHN0YWdlJTIwcmVoZWFyc2FsfGVufDB8fHx8MTc4MjYxNzEzMHww&ixlib=rb-4.1.0&q=85";

const VARIANTS = {
  whitefire: {
    program: "whitefire",
    root: "/",
    venue: "Online",
    venueSubline: "Live, from anywhere",
    scheduleLabel: "Sundays · 10:00 AM – 1:00 PM Pacific",
    parking: null,
    showTanna: true,
    joinPath: "/join",
    billing: "Coached by Eliza Roberts & Tanna Frederick — Featuring Eric Roberts",
    contact: { type: "email" },
  },
  tvi: {
    program: "tvi",
    root: "/tvi",
    venue: "Two Roads Theatre, 4348 Tujunga Ave, Studio City, CA 91604",
    scheduleLabel: "Saturdays · 10:00 AM – 2:00 PM",
    parking: "Come early to get creative with Parking",
    showTanna: false,
    joinPath: null,
    billing: "Featuring Eric Roberts — Academy Award nominee",
    contact: {
      type: "tvi",
      name: "Alan S Nusbaum — TVI Studios",
      email: "alan.nusbaum@tvistudios.com",
      phone: "818.784.6500",
      phoneHref: "tel:+18187846500",
      subline: "Registration and student contact for the TVI program runs through TVI Studios.",
    },
  },
};

// Public rendering only — the Sunday (whitefire) class is held online; the class record itself is untouched.
const publicTitle = (c) => (c.program === "whitefire" ? c.title.replace(/\s*[-–—]\s*Whitefire\s*$/, "") : c.title);
const publicVenue = (c) => (c.program === "whitefire" ? "Online · Live, from anywhere" : c.venue);

export default function Landing({ variant = "whitefire" }) {
  const cfg = VARIANTS[variant] || VARIANTS.whitefire;
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [studioEmail, setStudioEmail] = useState("workingactor.studio@gmail.com");

  useEffect(() => {
    api.get("/public/classes", { params: { program: cfg.program } }).then((r) => setClasses(r.data)).catch(() => {});
    api.get("/public/studio").then((r) => setStudioEmail(r.data.studio_email)).catch(() => {});
  }, [cfg.program]);

  return (
    <div className="min-h-screen bg-paper text-ink">
      {/* Top bar */}
      <header className="border-b border-hairline">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 flex h-16 items-center justify-between">
          <Link to={cfg.root} className="flex items-center gap-3" data-testid="nav-home-logo">
            <Monogram size={40} />
            <span className="font-serif text-lg" style={{ fontWeight: 600 }}>
              Working Actor
            </span>
          </Link>
          <div className="flex items-center gap-3 sm:gap-4">
            <Link to={cfg.root} className="btn-ghost text-sm hidden sm:inline-flex" data-testid="nav-home">
              Home
            </Link>
            {cfg.joinPath && (
              <Link to={cfg.joinPath} className="btn-ghost text-sm" data-testid="nav-join-class">
                Join the class
              </Link>
            )}
            <a href="#rehearsal-room" className="btn-ghost text-sm leading-tight text-center" data-testid="nav-rehearsal-room">
              The Rehearsal Room
            </a>
            <Link to="/login" className="btn-ghost text-sm" data-testid="nav-login">
              Log in
            </Link>
            <button onClick={() => navigate("/signup")} className="btn-brass text-sm" data-testid="nav-signup">
              Request a spot
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="animate-fade-up">
            <Monogram size={72} className="mb-8" />
            <p className="overline mb-5">The Acting Studio of Eliza Roberts</p>
            <h1 className="font-serif text-ink tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-7xl">
              Craft, taught by a<br />
              <span className="italic text-brass">working actor.</span>
            </h1>
            <p className="mt-8 max-w-md font-sans text-base text-ink/80 leading-relaxed">
              An intimate setting for the real work — Cold Reads can be the Warmest, Cheating Dialogue, Improving
              through Improv, Scene Un-Study, Making your agent fall in serious love with you and much more
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <button onClick={() => navigate(cfg.joinPath || "/signup")} className="btn-brass" data-testid="hero-request-spot">
                Request a spot
              </button>
              <a href="#classes" className="btn-outline">
                See open classes
              </a>
            </div>
            <a
              href="#eric-roberts"
              className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-stone hover:text-ink transition-colors"
              data-testid="hero-featuring-line"
            >
              <span className="inline-block w-8 h-px bg-brass" />
              {cfg.billing}
            </a>
          </div>

          <div className="relative">
            <div className="border border-hairline p-3 bg-white">
              <img
                src={HERO_IMG}
                alt="Performers working a scene on a darkened stage"
                className="w-full h-[380px] sm:h-[460px] object-cover"
                style={{ filter: "grayscale(8%)" }}
              />
            </div>
            <div className="absolute -bottom-4 -left-4 bg-paper border border-hairline px-5 py-3 hidden sm:block">
              <p className="font-mono text-[11px] uppercase tracking-wider text-stone">Now in session</p>
              <p className="font-serif text-xl text-ink leading-none mt-1">Scene Study</p>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="brass-rule" />
      </div>

      {/* What this studio is */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-20">
        <div className="grid md:grid-cols-3 gap-10">
          <div>
            <p className="overline mb-3">01 — In the room</p>
            <h3 className="font-serif text-2xl text-ink mb-3">Work, not workshops</h3>
            <p className="font-sans text-sm text-ink/75 leading-relaxed">
              Every session is on its feet. You bring sides, we run them — cold reads, two-handers, the kind of
              material you'd actually get sent.
            </p>
          </div>
          <div>
            <p className="overline mb-3">02 — Off-book</p>
            <h3 className="font-serif text-2xl text-ink mb-3">The double meaning of Reps</h3>
            <p className="font-sans text-sm text-ink/75 leading-relaxed">
              In class, discover the double meaning of the oft-heard term 'Reps.' Sides are posted to your portal
              ahead of time, or bring your own. To prepare or not to prepare? That is the question.
            </p>
          </div>
          <div>
            <p className="overline mb-3">03 — The take</p>
            <h3 className="font-serif text-2xl text-ink mb-3">Notes that travel</h3>
            <p className="font-sans text-sm text-ink/75 leading-relaxed">
              The work in class is the same work that holds up on a self-tape and in the callback. Specific,
              repeatable, yours.
            </p>
          </div>
        </div>
      </section>

      {/* Meet your coach — Eliza Roberts (primary face of the studio) */}
      <section id="meet-your-coach" className="mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-24">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Content */}
          <div className="lg:col-span-6 order-2 lg:order-1">
            <p className="overline mb-4">Meet your coach</p>
            <h2 className="font-serif text-5xl sm:text-6xl text-ink tracking-tight leading-[0.95]">
              Eliza Roberts
            </h2>
            <p className="font-sans text-base text-ink/80 leading-relaxed mt-6 max-w-xl">
              Eliza Roberts has spent a lifetime on stage, on set, and in casting offices. Her coaching is full
              spectrum. We address the acting itself, blending techniques. We also address the way the business
              works — answering the questions that intrigue the students.
            </p>

            <div className="mt-8 space-y-4 max-w-xl">
              <div data-testid="eliza-credit-acting">
                <p className="overline mb-1.5">Acting</p>
                <p className="font-sans text-sm text-ink/80 leading-relaxed">
                  National Lampoon's ANIMAL HOUSE; Superman's mother on LOIS &amp; CLARK; decades of film &amp;
                  television.
                </p>
              </div>
              <div className="hair-rule" />
              <div data-testid="eliza-credit-camera">
                <p className="overline mb-1.5">Behind the camera</p>
                <p className="font-sans text-sm text-ink/80 leading-relaxed">
                  Casting director, producer, and longtime manager to Eric Roberts.
                </p>
              </div>
              <div className="hair-rule" />
              <div data-testid="eliza-credit-roots">
                <p className="overline mb-1.5">Roots</p>
                <p className="font-sans text-sm text-ink/80 leading-relaxed">
                  Daughter of writer-director Lila Garrett, screenwriter David Rayfiel, and publicist Don Garrett.
                </p>
              </div>
            </div>

            <p className="font-serif italic text-2xl text-ink/70 leading-snug mt-8 max-w-xl border-l-2 border-brass pl-5">
              The studio is built around her — so she can teach, and we handle the rest.
            </p>
          </div>

          {/* Images — lead + secondary companion */}
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative max-w-sm mx-auto lg:ml-auto lg:mr-0 mb-10 lg:mb-0">
              <div className="border border-hairline p-3 bg-white">
                <div className="aspect-[3/4.5] overflow-hidden bg-hairline/30">
                  <img
                    src="/eliza-lead-v2.jpg"
                    alt="Eliza Roberts holding a script by a window"
                    className="w-full h-full object-cover object-center"
                    style={{ filter: "grayscale(6%)" }}
                    data-testid="eliza-portrait-lead"
                  />
                </div>
              </div>
              <div className="absolute -bottom-8 -left-6 w-32 sm:w-44 hidden sm:block">
                <div className="border border-hairline p-2 bg-white shadow-sm">
                  <div className="aspect-[3/4.5] overflow-hidden bg-hairline/30">
                    <img
                      src="/eliza-companion-v3.jpg"
                      alt="Eliza Roberts laughing"
                      className="w-full h-full object-cover object-center"
                      style={{ filter: "grayscale(6%)" }}
                      data-testid="eliza-portrait-secondary"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Faculty note — Tanna Frederick (Whitefire skin only) */}
      {cfg.showTanna && (
        <section id="faculty" className="mx-auto max-w-6xl px-4 sm:px-6 pb-8 sm:pb-16">
          <div className="brass-rule mb-12" />
          <div className="grid sm:grid-cols-12 gap-8 items-start" data-testid="tanna-frederick">
            <div className="sm:col-span-3">
              <div className="border border-hairline p-2 bg-white max-w-[180px]">
                <div className="aspect-[3/4] overflow-hidden bg-hairline/30">
                  <img
                    src="/tanna-frederick.jpg"
                    alt="Tanna Frederick"
                    className="w-full h-full object-cover"
                    style={{ filter: "grayscale(6%)" }}
                    data-testid="tanna-portrait"
                  />
                </div>
              </div>
            </div>
            <div className="sm:col-span-9">
              <p className="overline mb-3">Also on faculty</p>
              <p className="font-serif text-2xl sm:text-3xl text-ink leading-snug max-w-xl">
                Tanna Frederick — teaches the online / streaming portion of Working Actor.
              </p>
              <p className="font-sans text-base text-ink/80 leading-relaxed max-w-xl mt-4">
                An award-winning actress, director, and producer, Tanna Frederick is a working actor in the truest
                sense — seven features with Henry Jaglom, a Best Actress win for <em>South of Hope Street</em>, and
                the director's chair on <em>Death Pays Flora A Visit</em> (starring Eric Roberts). On stage: LA
                Stage's Best Actress for <em>The Rainmaker</em>, and her one-woman show, <em>Lion Eyes</em>.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Eric Roberts — the working actor's working actor */}
      <section id="eric-roberts" className="bg-ink text-paper">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-24">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Portrait */}
            <div className="lg:col-span-5">
              <div className="border border-brass/30 p-3 bg-ink max-w-sm mx-auto lg:mx-0">
                <div
                  className="aspect-[3/4.5] relative overflow-hidden bg-[#111]"
                  data-testid="eric-portrait"
                >
                  <img
                    src="/eric-roberts.jpg"
                    alt="Eric Roberts"
                    className="absolute inset-0 w-full h-full object-cover object-center"
                    style={{ filter: "grayscale(4%)" }}
                  />
                </div>
              </div>
              <p className="font-mono text-[11px] text-paper/40 mt-3 text-center lg:text-left max-w-sm mx-auto lg:mx-0">
                Photo by Paul Gregory.
              </p>
            </div>

            {/* Content */}
            <div className="lg:col-span-7">
              <p className="overline !text-brass mb-4">Endorsed by Eric Roberts</p>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-paper tracking-tight leading-[0.95]">
                In the corner of every<br />
                <span className="italic text-brass">working actor.</span>
              </h2>
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-paper/50 mt-6">
                Eric Roberts · Academy Award nominee · Runaway Train
              </p>
              <p className="font-sans text-base text-paper/80 leading-relaxed mt-6 max-w-xl">
                Eric Roberts has built one of the most prolific careers in screen history — an Academy Award nominee
                for <span className="italic">RUNAWAY TRAIN</span>, with hundreds of credits across five decades. He
                is, by any measure, the working actor's working actor.
              </p>

              {/* Endorsement — Eric Roberts, approved quote */}
              <blockquote
                className="border-l-2 border-brass pl-5 mt-8 max-w-xl"
                data-testid="eric-endorsement"
              >
                <p className="font-serif italic text-2xl text-paper/85 leading-snug">
                  "I'll never stop learning, as an actor. And teaching is the most glorious way to learn."
                </p>
                <p className="font-mono text-xs uppercase tracking-[0.18em] text-brass mt-4">
                  — Eric Roberts
                </p>
              </blockquote>

              {/* The book */}
              <div className="mt-10 border border-brass/30 p-6 max-w-xl flex flex-col sm:flex-row sm:items-center gap-5">
                <BookOpen size={28} className="text-brass shrink-0" />
                <div className="flex-1">
                  <p className="overline !text-brass/70 mb-1">The book</p>
                  <p className="font-serif text-xl text-paper italic leading-tight">
                    Runaway Train: or, The Story of My Life So Far
                  </p>
                  <p className="font-mono text-xs text-paper/50 mt-1">St. Martin's Press, 2024</p>
                </div>
                <a
                  href="#"
                  className="btn-outline text-sm !border-brass !text-brass hover:!bg-brass/10 shrink-0"
                  data-testid="eric-book-link"
                >
                  Find the book
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Open classes */}
      <section id="classes" className="bg-white border-y border-hairline">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-20">
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className="overline mb-2">The schedule</p>
              <h2 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight">Open classes</h2>
            </div>
          </div>

          <div className="mb-10 flex flex-col gap-1.5" data-testid="schedule-info">
            <p className="font-mono text-sm text-ink flex items-center gap-2" data-testid="venue-line">
              <MapPin size={14} className="text-brass shrink-0" /> {cfg.venue}
            </p>
            {cfg.venueSubline && (
              <p className="font-sans text-xs text-stone" data-testid="venue-subline">{cfg.venueSubline}</p>
            )}
            <p className="font-mono text-sm text-stone" data-testid="schedule-line">{cfg.scheduleLabel}</p>
            {cfg.parking && (
              <p className="font-mono text-xs tracking-wide text-garnet mt-1" data-testid="parking-note">
                {cfg.parking}
              </p>
            )}
          </div>

          {classes.length === 0 ? (
            <p className="font-sans text-sm text-stone">No open classes posted right now. Check back soon.</p>
          ) : (
            <div className="divide-y divide-hairline border-t border-hairline">
              {classes.map((c) => (
                <div
                  key={c.id}
                  className="grid sm:grid-cols-12 gap-4 sm:gap-6 items-center py-6"
                  data-testid={`public-class-${c.id}`}
                >
                  <div className="sm:col-span-2 font-mono text-sm text-ink">
                    {cfg.program === "whitefire" ? "November enrollment open" : formatDate(c.date)}
                  </div>
                  <div className="sm:col-span-5">
                    <p className="font-serif text-2xl text-ink leading-tight">{publicTitle(c)}</p>
                    <p className="font-sans text-sm text-stone flex items-center gap-1.5 mt-1">
                      <MapPin size={13} /> {publicVenue(c)}
                    </p>
                  </div>
                  <div className="sm:col-span-2 font-mono text-sm text-stone">
                    {formatTime(c.start_time)}
                  </div>
                  <div className="sm:col-span-2 font-mono text-sm">
                    {c.open_seats > 0 ? (
                      <span className="text-confirmed">{c.open_seats} seats left</span>
                    ) : (
                      <span className="text-garnet">Waitlist</span>
                    )}
                  </div>
                  <div className="sm:col-span-1 flex sm:justify-end">
                    <button
                      onClick={() => navigate("/signup")}
                      className="btn-ghost text-brass text-sm font-semibold"
                      data-testid={`request-spot-${c.id}`}
                    >
                      Request <ArrowUpRight size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* In good company — names only, restrained */}
      <section className="bg-white border-y border-hairline">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-20">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center mb-12">
            <div className="lg:col-span-5">
              <div className="border border-hairline p-3 bg-white max-w-md mx-auto lg:mx-0">
                <img
                  src="/eliza-garden-v1.jpg"
                  alt="Eliza Roberts in the garden"
                  className="w-full h-auto object-cover"
                  style={{ filter: "grayscale(6%)" }}
                  data-testid="eliza-garden-photo"
                />
              </div>
            </div>
            <div className="lg:col-span-7">
              <p className="overline mb-2">In good company</p>
              <h2 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight">Coached by Eliza</h2>
              <p className="font-sans text-sm text-ink/70 mt-4 leading-relaxed">
                Over a long career, Eliza has helped develop the work and careers of many artists — most without
                recognizable names, all of them outstanding — and some you'd know.
              </p>
            </div>
          </div>
          <div className="border-t border-hairline" data-testid="company-names">
            {[
              "David Duchovny",
              "George Clooney",
              "Halle Berry",
              "Jeffrey Dean Morgan",
              "Keaton Simons",
              "Matt Rife",
              "West Duchovny",
            ].map((name) => (
              <div
                key={name}
                className="flex items-baseline gap-4 py-4 border-b border-hairline"
                data-testid={`company-name-${name.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <span className="inline-block w-5 h-px bg-brass translate-y-[-0.3em]" />
                <span className="font-serif text-2xl sm:text-3xl text-ink leading-none">{name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Rehearsal Room */}
      <section id="rehearsal-room" className="mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-20" data-testid="rehearsal-room-section">
        <div className="max-w-xl">
          <h2 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight">The Rehearsal Room</h2>
          <p className="font-sans text-base text-ink/80 leading-relaxed mt-6">
            A scene partner for the hours when nobody's around to run lines. Paste in your sides, start talking,
            and the page turns itself — it listens, and when you say your line, it gives you your cue. Off-book
            gets a lot less lonely. Free for students right now.
          </p>
          <a
            href="https://rehearse.workingactor.studio/blank"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-brass mt-8"
            data-testid="rehearsal-room-link"
          >
            Run your sides
          </a>
        </div>
      </section>

      {/* Contact */}
      <section className="bg-ink text-paper">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-16 text-center">
          <Monogram size={56} className="mx-auto !border-brass !text-brass mb-6" />
          <p className="overline !text-brass mb-3">Get in touch</p>
          <h2 className="font-serif text-4xl text-paper mb-4">Reach the studio</h2>
          {cfg.contact.type === "email" ? (
            <>
              <a
                href={`mailto:${studioEmail}`}
                className="font-mono text-lg text-brass hover:underline"
                data-testid="studio-email"
              >
                {studioEmail}
              </a>
              <p className="font-sans text-xs text-paper/50 mt-3 max-w-sm mx-auto">
                All studio correspondence comes from this address on behalf of Eliza Roberts.
              </p>
            </>
          ) : (
            <>
              <p
                className="font-mono text-xs uppercase tracking-[0.18em] text-paper/70 mb-3"
                data-testid="tvi-contact-name"
              >
                {cfg.contact.name}
              </p>
              <a
                href={`mailto:${cfg.contact.email}`}
                className="block font-mono text-lg text-brass hover:underline"
                data-testid="tvi-contact-email"
              >
                {cfg.contact.email}
              </a>
              <a
                href={cfg.contact.phoneHref}
                className="block font-mono text-lg text-brass hover:underline mt-1"
                data-testid="tvi-contact-phone"
              >
                {cfg.contact.phone}
              </a>
              <p className="font-sans text-xs text-paper/50 mt-3 max-w-sm mx-auto">
                {cfg.contact.subline}
              </p>
            </>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-paper border-t border-hairline">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Monogram size={36} />
            <span className="font-serif text-base text-ink" style={{ fontWeight: 600 }}>
              Working Actor
            </span>
          </div>
          <p className="font-sans text-xs text-stone">
            © {new Date().getFullYear()} The Acting Studio of Eliza Roberts.
          </p>
        </div>
      </footer>
    </div>
  );
}
